/**
 * @author        denys.yefremov@startupsoft.co (Denys Yefremov)
 * @copyright     Copyright (c) 2022, Zumper
 */

import type { Media } from '@zcode/bizos'
import { keepBetaParam } from '@zcode/nr-utils'
import { trackPreviousLocationInHistoryState } from '@zcode/router-utils'
import type { Listable, Property } from '@zcode/state-type'
import { hyphenize } from '@zcode/utils'
import type { Location } from 'history'
import { selectHasRoomPropertyType } from '../../../../shared/bizos/rentable/selectors/floorplans'
import * as MediaSelectors from './media'

export function selectMedia(rentable: { media: readonly Media[] }): readonly Media[]
export function selectMedia(rentable: null | undefined): null
export function selectMedia(rentable: { media?: undefined }): undefined
export function selectMedia(
  rentable: { media: readonly Media[] } | { media?: undefined } | null | undefined
): readonly Media[] | null | undefined
export function selectMedia(
  rentable: { media: readonly Media[] } | { media?: undefined } | null | undefined
) {
  return rentable ? rentable.media : null
}
export function selectDisplayMedia(
  property: Pick<Property, 'media'> | { media: undefined } | undefined | null,
  noSort?: boolean
) {
  const media = selectMedia(property)
  return media ? MediaSelectors.selectDisplayMedia(media, noSort) : null
}

export const selectFloorplans = (rentable: Property) => (rentable ? rentable.floorplans : null)
export const selectHasRoomTypeFloorplans = ({
  floorplans,
}: Pick<Listable | Property, 'floorplans'>) => {
  return selectHasRoomPropertyType(floorplans)
}

export const selectGalleryUrl = (
  entity: Property,
  section?: 'Photos' | 'Videos' | 'Tours' | 'Highlight reel' | Pick<Media, 'id'>
) => {
  return trackPreviousLocationInHistoryState(
    keepBetaParam(
      `${entity.url}?gallery${
        section
          ? `#${typeof section === 'object' ? `media-${section.id}` : hyphenize(section)}`
          : ''
      }`
    )
  )
}

export const selectGalleryUrlFromLocation = (
  section?: 'Photos' | 'Videos' | 'Tours' | 'Highlight reel' | Pick<Media, 'id'>
) => {
  return trackPreviousLocationInHistoryState(
    keepBetaParam((location: Location<unknown>) => {
      const searchParams = new URLSearchParams(location.search)
      return `${location.pathname}?${searchParams.has('listing_id') ? `listing_id=${searchParams.get('listing_id')}&` : ''}gallery${
        section
          ? `#${typeof section === 'object' ? `media-${section.id}` : hyphenize(section)}`
          : ''
      }`
    })
  )
}

export const selectFocusedGalleryUrlFromLocation = (index: number) => {
  return trackPreviousLocationInHistoryState(
    keepBetaParam((location: Location<unknown>) => {
      const searchParams = new URLSearchParams(location.search)
      return `${location.pathname}?${searchParams.has('listing_id') ? `listing_id=${searchParams.get('listing_id')}&` : ''}gallery#focus=${index}`
    })
  )
}

export const hasHasGalleryParam = (search?: string) => {
  if (!search) {
    return false
  }

  return new URLSearchParams(search).has('gallery')
}
